import React, { useState } from 'react'
import {
  GroupOrderCancellationType,
  GroupOrderCancellationReason,
  getGroupOrderCancellationTypeLabel,
  getGroupOrderCancellationReasonLabel,
} from '../../../../../src/constants'

interface CancellationFormProps {
  numberOfChefs: number
  cancellationType: GroupOrderCancellationType
  setCancellationType: (v: GroupOrderCancellationType) => void

  cancellationReason: GroupOrderCancellationReason
  setCancellationReason: (v: GroupOrderCancellationReason) => void

  cancellationNotes: string
  setCancellationNotes: (v: string) => void
  handleCancel: () => void
  continueToNextSection: () => void
}

export const CancellationForm: React.FC<CancellationFormProps> = ({
  numberOfChefs,
  cancellationType,
  setCancellationType,
  cancellationReason,
  setCancellationReason,
  cancellationNotes,
  setCancellationNotes,
  handleCancel,
  continueToNextSection,
}) => {
  const [error, setError] = useState('')
  const handleContinue = () => {
    if (
      cancellationReason === GroupOrderCancellationReason.OTHER &&
      cancellationNotes.trim().length === 0
    ) {
      setError('Additional Information is required when reason is Other.')

      return
    }
    continueToNextSection()
  }

  return (
    <div>
      <div className="space-y-10">
        {/* Warning Banner */}
        <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-8 flex items-start">
          <svg
            className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-gray-800 font-medium">
            Warning: Once the status is changed to canceled, it cannot be
            reverted.
          </p>
        </div>

        {/* Cancellation Type */}
        <div className="mb-6">
          <label className="block text-gray-900 text-xl font-semibold mb-3">
            Cancellation Type
          </label>
          <div className="relative inline-block w-64">
            <select
              value={cancellationType}
              onChange={(e) =>
                setCancellationType(
                  e.target.value as GroupOrderCancellationType,
                )
              }
              className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:border-gray-400"
            >
              <option value={GroupOrderCancellationType.CLIENT}>
                {getGroupOrderCancellationTypeLabel(
                  GroupOrderCancellationType.CLIENT,
                )}
              </option>

              {numberOfChefs <= 1 && (
                <option value={GroupOrderCancellationType.CHEF}>
                  {getGroupOrderCancellationTypeLabel(
                    GroupOrderCancellationType.CHEF,
                  )}
                </option>
              )}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
          {numberOfChefs > 1 && (
            <p className="text-gray-600 text-sm mt-3">
              Multiple vendors scheduled. Vendor cancellation is not available.
            </p>
          )}
        </div>

        {/* Select Cancellation Reason */}
        <div className="mb-6">
          <label className="block text-gray-900 text-xl font-semibold mb-3">
            Select Cancellation Reason
          </label>
          <div className="relative inline-block w-64">
            <select
              value={cancellationReason}
              onChange={(e) =>
                setCancellationReason(
                  e.target.value as GroupOrderCancellationReason,
                )
              }
              className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:border-gray-400"
            >
              {Object.values(GroupOrderCancellationReason).map((reason) => (
                <option key={reason} value={reason}>
                  {getGroupOrderCancellationReasonLabel(reason)}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
              <svg className="fill-current h-4 w-4" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mb-8">
          <label className="block text-gray-900 text-xl font-semibold mb-3">
            Additional Information
            {cancellationReason === GroupOrderCancellationReason.OTHER && (
              <span className="text-red-600"> *</span>
            )}
          </label>
          {error && <p className="text-red-600">{error}</p>}
          <textarea
            value={cancellationNotes}
            onChange={(e) => setCancellationNotes(e.target.value)}
            placeholder="Add any additional details about the cancellation..."
            rows={6}
            maxLength={500}
            className={`w-full bg-white border text-gray-700 py-3 px-4 rounded-lg leading-relaxed focus:outline-none focus:border-gray-400 resize-none ${
              error ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        </div>
      </div>
      {/* Action Buttons */}
      <div className="mt-10 flex items-center gap-6">
        <button
          className="flex-1 h-16 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xl font-semibold transition-colors"
          onClick={handleContinue}
        >
          Continue
        </button>
        <button
          className="flex-1 h-16 rounded-xl border border-gray-200 bg-white hover:bg-gray-300 disabled:opacity-60 text-gray-900 text-xl font-semibold transition-colors"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
